import React, { ButtonHTMLAttributes } from 'react'
import styled from 'styled-components'

import theme from '@app/ui/theme'

interface ButtonProps extends Omit<ButtonHTMLAttributes<unknown>, 'style'> {
  className?: string
  variant?: 'main' | 'secondaryGreenText' | 'secondaryWhiteText' | 'small'
  text?: string
  children?: React.ReactNode
  isWideOnMobile?: boolean
  padding?: string
}

const StyledButton = styled.button<ButtonProps>`
  margin: 0;
  overflow: visible;
  font: inherit;
  -webkit-font-smoothing: inherit;
  -moz-osx-font-smoothing: inherit;
  -webkit-appearance: none;
  &::-moz-focus-inner {
    border: 0;
    padding: 0;
  }

  border-radius: 100rem;
  text-align: center;
  font-size: 1.5rem;
  font-weight: 500;
  line-height: 4.8rem;
  outline: 0;
  cursor: pointer;
  transition: all 0.3s;
  padding: ${({ padding }) => padding};

  ${({ variant }) => {
    switch (variant) {
      case 'secondaryGreenText':
        return `
          border: 1px solid ${theme.global.colors.secondaryGreenTextButtonBorder};
          background-color: ${theme.global.colors.secondaryGreenTextButtonBg};
          color: ${theme.global.colors.secondaryGreenTextButtonText};
        `
      case 'secondaryWhiteText':
        return `
          border: 1px solid ${theme.global.colors.secondaryWhiteTextButtonBorder};
          background-color: ${theme.global.colors.secondaryWhiteTextButtonBg};
          color: ${theme.global.colors.secondaryWhiteTextButtonText};
        `
      case 'small':
        return `
          width: 13.3rem;
          padding: 0;
          border: 1px solid ${theme.global.colors.smallButtonBorder};
          font-size: 1.3rem;
          line-height: 4.4rem;
          background-color: ${theme.global.colors.smallButtonBg};
          color: ${theme.global.colors.smallButtonText};
        `
      default:
        return `
          border: 1px solid ${theme.global.colors.mainButtonBorder};
          background-color: ${theme.global.colors.mainButtonBg};
          color: ${theme.global.colors.mainButtonText};
        `
    }
  }};

  :hover {
    ${({ variant }) => {
      switch (variant) {
        case 'secondaryGreenText':
          return `
            border-color: ${theme.global.colors.secondaryGreenTextButtonBorderHover};
            background-color: ${theme.global.colors.secondaryGreenTextButtonBgHover};
          `
        case 'secondaryWhiteText':
          return `
            border-color: ${theme.global.colors.secondaryWhiteTextButtonBorderHover};
            background-color: ${theme.global.colors.secondaryWhiteTextButtonBgHover};
          `
        case 'small':
          return `
            border-color: ${theme.global.colors.smallButtonBorderHover};
            background-color: ${theme.global.colors.smallButtonBgHover};
          `
        default:
          return `
            border-color: ${theme.global.colors.mainButtonBorderHover};
            background-color: ${theme.global.colors.mainButtonBgHover};
          `
      }
    }};
  }

  :disabled {
    cursor: default;

    ${({ variant }) => {
      switch (variant) {
        case 'secondaryGreenText':
          return `
            border-color: ${theme.global.colors.halfSpanishWhite};
            color: ${theme.global.colors.napa};
            background-color: ${theme.global.colors.transparent};
          `
        case 'secondaryWhiteText':
          return `
            color: ${theme.global.colors.greenPea};
            background-color: ${theme.global.colors.transparent};
            border-color: ${theme.global.colors.greenPea};
          `
        case 'small':
          return `
            color: ${theme.global.colors.napa};
          `
        default:
          return `
            color: ${theme.global.colors.napa};
            background-color: ${theme.global.colors.halfSpanishWhite};
            border-color: ${theme.global.colors.halfSpanishWhite};
          `
      }
    }};
  }

  :active {
    ${({ variant }) => {
      switch (variant) {
        case 'secondaryGreenText':
          return `
            color: ${theme.global.colors.white};
            background-color: ${theme.global.colors.elemyGreen};
            border-color: ${theme.global.colors.elemyGreen};
          `
        case 'secondaryWhiteText':
          return `
          color: ${theme.global.colors.white};
            background-color: ${theme.global.colors.transparent};
            border-color: ${theme.global.colors.greenPea};
          `
        case 'small':
          return `
            color: ${theme.global.colors.napa};
          `
        default:
          return `
            color: ${theme.global.colors.napa};
            background-color: ${theme.global.colors.halfSpanishWhite};
            border-color: ${theme.global.colors.halfSpanishWhite};
          `
      }
    }};
  }

  @media ${({ theme }) => theme.device.mobileL} {
    ${({ isWideOnMobile }) => isWideOnMobile && 'width: 100%;'}
  }
`

export function Button({ onClick, text, padding = '0.2rem 3.1rem', children, className, ...rest }: ButtonProps) {
  return (
    <StyledButton onClick={onClick} padding={padding} className={className} {...rest}>
      {text || children}
    </StyledButton>
  )
}
